import { defineStore } from "pinia";
import { ref } from "vue";
import { useApolloClient } from "@vue/apollo-composable";
import gql from "graphql-tag";

interface Article {
  id: string;
  title: string;
  excerpt: string;
  excerptHtml: string;
  content: string;
  contentHtml: string;
  altText?: string;
  image: { src: string };
  publishedAt: string;
  handle: string;
  tags: string[];
  coverImage?: {
    value?: string;      // GID (ex: "gid://shopify/MediaImage/123")
    reference?: any;     // Här lägger vi in den fullständiga MediaImage-datan
  };
  wordsBy?: string; 

}

interface Blog {
  title: string;
  handle: string;
  articles: Article[];
  seo?: {
    title?: string;
    description?: string;
  };
}

interface MediaImageModel {
  id: string;
  image: {
    url: string;
    altText?: string | null;
  };
  presentation: {
    asJson?: string | null;
  };
}


const SINGLE_BLOG_QUERY = gql`
  query BlogByHandle($handle: String!) {
    blogByHandle(handle: $handle) {
      title
      handle
      seo {            
          title
          description
      }
      articles(first: 100, sortKey: PUBLISHED_AT) {
        edges {
          node {
            id
            title
            excerpt
            excerptHtml
            content
            contentHtml
            image {
              id
              url
              src
              altText
            }
            publishedAt
            handle
            tags,
            coverImage: metafield(namespace: "custom", key: "coverimage") {
                value
            }
            wordsBy: metafield(namespace: "custom", key: "words_by") {
                value
            }
            seo {            
                title
                description
            }
          }
        }
      }
    }
  }
`;



const SINGLE_ARTICLE_QUERY = gql`
  query SingleArticleById($id: ID!) {
    node(id: $id) {
      ... on Article {
        id
        title
        excerpt
        excerptHtml
        content
        contentHtml
        image {
          src
          altText
        }
        publishedAt
        handle
        tags
        coverImage: metafield(namespace: "custom", key: "coverimage") {
          value
        }
        wordsBy: metafield(namespace: "custom", key: "words_by") {
                value
        }
      }
    }
  }
`;

const GET_MEDIA_IMAGE_BY_ID = gql`
  query MediaImageWithFocal($id: ID!) {
    node(id: $id) {
      ... on MediaImage {
        id
        image {
          url
          altText
        }
        presentation {
          asJson(format: IMAGE)
        }
      }
    }
  }
`;

export const useBlogStore = defineStore("blog", () => {
  // Reactive objekt för att lagra bloggar och artiklar med handle som nyckel
  const blogsByHandle = ref<Record<string, Blog>>({});
  const articlesByHandle = ref<Record<string, Article>>({});
  const mediaImagesById = ref<Record<string, MediaImageModel>>({});
  const articlesById = ref<Record<string, Article>>({});


  // Sätter en artikel i storen
  function setArticleByHandle(article: Article) {
    articlesByHandle.value[article.handle] = article;
  }

  function setArticleById(article: Article) {
    articlesById.value[article.id] = article;
  }

  // Sätter en blogg i storen och loopar igenom dess artiklar för att spara dem individuellt
  function setBlogByHandle(blog: Blog) {
    blogsByHandle.value[blog.handle] = blog;
    blog.articles.forEach((article: Article) => {
      setArticleByHandle(article);
    });
  }

  // Getter för en blogg via handle
  function getBlogByHandle(handle: string): Blog | undefined {
    return blogsByHandle.value[handle];
  }

  // Getter för en artikel via handle

  async function getArticleByHandle(handle: string): Promise<Article | undefined> {
    // 1) Hämta artikeln från store
    const originalArticle = articlesByHandle.value[handle];
    if (!originalArticle) {
      // Finns ingen artikel i storen för detta handle
      return undefined;
    }

    // 2) Kopiera artikeln och dess coverImage (shallow copy)
    //    så att vi kan lägga till nya properties (t.ex. .reference)
    const newArticle: Article = {
      ...originalArticle,
      coverImage: originalArticle.coverImage
        ? { ...originalArticle.coverImage }
        : undefined,
    };

    // 3) Om det finns ett GID (value) men ingen reference ännu, hämta media
    const coverImageId = newArticle.coverImage?.value;
    if (coverImageId && !newArticle.coverImage?.reference) {
      try {
        const { client } = useApolloClient();
        const { data } = await client.query({
          query: GET_MEDIA_IMAGE_BY_ID, // node(id: $coverImageId) { ... on MediaImage {...} }
          variables: { id: coverImageId },
        });

        // Om vi verkligen fick ut en MediaImage, lägg in den i newArticle.coverImage.reference
        if (data?.node && newArticle.coverImage) {
          newArticle.coverImage.reference = data.node;
        }
      } catch (error) {
        console.warn("Kunde inte hämta coverImage MediaImage:", error);
      }
    }

    // 4) Uppdatera storen med den nya artikeln (så vi slipper extra anrop nästa gång)
    articlesByHandle.value[handle] = newArticle;

    // 5) Returnera den uppdaterade artikeln
    return newArticle;
  }

  async function fetchArticleById(id: string): Promise<Article | null> {
    if (articlesById.value[id]) {
      return articlesById.value[id];
    }
  
    try {
      const { client } = useApolloClient();
      const { data } = await client.query({
        query: gql`
          query getArticleById($id: ID!) {
            node(id: $id) {
              ... on Article {
                id
                title
                excerpt
                excerptHtml
                content
                contentHtml
                image {
                  src
                  altText
                }
                publishedAt
                handle
                tags
                coverImage: metafield(namespace: "custom", key: "coverimage") {
                  value
                }
                wordsBy: metafield(namespace: "custom", key: "words_by") {
                  value
                }
              }
            }
          }
        `,
        variables: { id },
      });
  
      if (data?.node?.__typename === 'Article') {
        const node = data.node;
  
        const article: Article = {
          id: node.id,
          title: node.title,
          excerpt: node.excerpt,
          excerptHtml: node.excerptHtml,
          content: node.content,
          contentHtml: node.contentHtml,
          image: node.image,
          altText: node.image?.altText,
          publishedAt: node.publishedAt,
          handle: node.handle,
          tags: node.tags,
          coverImage: node.coverImage?.value
            ? { value: node.coverImage.value }
            : undefined ,
          wordsBy: node.wordsBy?.value,

        };
  
        // Hämta MediaImage om nödvändigt
        const coverImageId = article.coverImage?.value;
        if (coverImageId) {
          try {
            const { data: mediaData } = await client.query({
              query: GET_MEDIA_IMAGE_BY_ID,
              variables: { id: coverImageId },
            });
            if (mediaData?.node && article.coverImage) {
              article.coverImage.reference = mediaData.node;
            }
          } catch (mediaError) {
            console.warn("Kunde inte hämta coverImage MediaImage:", mediaError);
          }
        }
  
        // Spara i båda mapparna
        articlesById.value[id] = article;
        articlesByHandle.value[article.handle] = article;
  
        return article;
      }
  
      return null;
    } catch (error) {
      console.error("Error fetching article by ID:", error);
      return null;
    }
  }
  


  // Hämtar en blogg via Apollo och sätter den i storen
  async function fetchBlogByHandle(blogHandle: string): Promise<Blog | null> {
    const cacheKey = blogHandle;
    
    if (blogsByHandle.value[cacheKey]) {
      return blogsByHandle.value[cacheKey];
    }
  
    try {
      const { client } = useApolloClient();
      const { data } = await client.query({
        query: SINGLE_BLOG_QUERY,
        variables: { handle: blogHandle },
        fetchPolicy: 'network-only',
      });
  
      if (data && data.blogByHandle) {
        const blogData = data.blogByHandle;
  
        const articles = await Promise.all(
          blogData.articles.edges.map(async (edge: any) => {
            const article = edge.node;
            const coverImageId = article.coverImage?.value;
  
            let coverImage: any = coverImageId ? { value: coverImageId } : undefined;
  
            // Hämta och bygga ut coverImage med riktig media-info
            // Hämta MediaImage om ID finns
            if (coverImageId) {
              try {
                const reference = await fetchMediaImageByID(coverImageId);
                if (reference) {
                  coverImage = { ...coverImage, reference };
                }
              } catch (mediaError) {
                console.warn(`Kunde inte hämta coverImage för artikel ${article.id}:`, mediaError);
              }
            }
  
            return {
              id: article.id,
              title: article.title,
              excerpt: article.excerpt,
              excerpthtml: article.excerptHtml,
              content: article.content,
              contenthtml: article.contentHtml,
              image: article.image?.url,
              altText: article.image?.altText ?? undefined,
              imageid: article.image?.id,
              publishedAt: article.publishedAt,
              handle: article.handle,
              tags: article.tags,              
              coverImage,
              wordsBy: article.wordsBy?.value,
            };
          })
        );
  
        const blog: Blog = {
          title: blogData.title,
          handle: blogData.handle,
          articles,
          seo: {
            title: blogData.seo?.title ?? undefined,
            description: blogData.seo?.description ?? undefined,
          },
        };
  
        setBlogByHandle(blog);
        return blog;
      } else {
        console.warn(`No blog data found for handle: ${blogHandle}`);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching blog with handle ${blogHandle}:`, error);
      return null;
    }
  }
  
  
  

  async function fetchMediaImageByID(mediaId: string): Promise<MediaImageModel | null> {
    // Kolla om vi redan har bilden i cache
    if (mediaImagesById.value[mediaId]) {
      return mediaImagesById.value[mediaId];
    }
    try {
      const { client } = useApolloClient();
      const { data } = await client.query({
        query: GET_MEDIA_IMAGE_BY_ID,
        variables: { id: mediaId },
      });

      // Se om vi fick en MediaImage
      if (data?.node?.__typename === "MediaImage") {
        const mediaImage = data.node as MediaImageModel;

        // Spara i storen (cacha) 
        mediaImagesById.value[mediaId] = mediaImage;

        // Returnera hela objektet (eller en mindre del om du vill)
        return mediaImage;
      } else {
        console.warn(`No MediaImage found for ID ${mediaId}.`, data);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching MediaImage with ID ${mediaId}:`, error);
      return null;
    }
  }

  function filterArticlesByTag(blog: Blog, tag: string): Article[] {
    return blog.articles.filter((article: Article) => article.tags.includes(tag));
  }

  function articleHasTag(article: Article, tag: string): boolean {
    return article.tags.includes(tag);
  }

  return {
    blogsByHandle,
    articlesByHandle,
    setBlogByHandle,
    setArticleByHandle,
    getBlogByHandle,
    getArticleByHandle,
    fetchBlogByHandle,
    fetchArticleById,
    fetchMediaImageByID,
    filterArticlesByTag,
    articleHasTag

  };
});
